import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { useCallback, useEffect, useRef, useState } from "react"
import { extraColors } from "../../utils/theme"
import { dayjsUTC, formatLocalDateTime } from "../../utils/dates"
import { V2_ACCENT, V2_FONT_MONO, V2_SURFACE } from "./v2/biothreatV2Tokens"
import type { ArrivalNode } from "./gdeltClusterAnalytics"

const TRACK_HEIGHT = 64
const MIN_DOT = 7
const MAX_DOT = 17
/** Tightest zoom window — an hour across the full track width. */
const MIN_SPAN_MS = 60 * 60 * 1000
/** Multiplier per +/- click. */
const BUTTON_STEP = 1.6
/** Multiplier per wheel notch. */
const WHEEL_STEP = 1.2

interface View {
  start: number
  end: number
}

const clamp = (value: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, value))

/** Dot diameter grows with how many articles landed at that timestamp. */
const dotSize = (count: number): number =>
  Math.min(MAX_DOT, MIN_DOT + (count - 1) * 2)

const positionPct = (t: number, view: View): number =>
  view.end <= view.start
    ? 50
    : ((t - view.start) / (view.end - view.start)) * 100

function ArrivalTooltip({ node }: { node: ArrivalNode }) {
  return (
    <Box sx={{ p: 0.5, maxHeight: 260, overflowY: "auto" }}>
      <Typography
        sx={{
          fontFamily: V2_FONT_MONO,
          fontSize: "0.65rem",
          letterSpacing: "0.06em",
          color: V2_ACCENT.cyan,
          mb: 0.75,
        }}
      >
        {formatLocalDateTime(dayjsUTC(node.t))} · {node.count}{" "}
        {node.count === 1 ? "article" : "articles"}
      </Typography>
      <Stack spacing={0.75}>
        {node.articles.map((a, i) => (
          <Box key={`${a.url ?? a.title}-${i}`}>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: extraColors.white,
                lineHeight: 1.3,
              }}
            >
              {a.title}
            </Typography>
            <Typography
              sx={{ fontSize: "0.6rem", color: extraColors.translucent50 }}
            >
              {a.sourceName}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

/**
 * A horizontal time axis with one dot per distinct publish time (dedupe of
 * same-timestamp arrivals; size encodes how many landed together; hover lists
 * them). An optional dotted marker shows the projected current-day volume.
 *
 * The axis is zoomable: scroll to zoom (anchored under the cursor), +/- and
 * reset controls, and drag-to-pan once zoomed in. The visible window lives in
 * `view`; re-mount (via a `key` on the range) resets it for a new date range.
 */
export function GdeltArrivalTimeline({
  nodes,
  rangeStart,
  rangeEnd,
  projectedLabel,
}: {
  nodes: ArrivalNode[]
  rangeStart: number
  rangeEnd: number
  projectedLabel?: string | null
}) {
  const fullSpan = Math.max(1, rangeEnd - rangeStart)
  const [view, setView] = useState<View>(() => ({
    start: rangeStart,
    end: rangeEnd,
  }))
  const trackRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{
    pointerId: number
    clientX: number
    view: View
  } | null>(null)
  const [dragging, setDragging] = useState(false)

  const span = view.end - view.start
  const isZoomed = span < fullSpan - 1
  const canZoomIn = span > MIN_SPAN_MS

  // Zoom by `factor` (<1 in, >1 out) while keeping the time under `fraction`
  // (0=left … 1=right of the track) pinned in place. Computed inside the state
  // updater so wheel events never read a stale view.
  const zoomAtFraction = useCallback(
    (fraction: number, factor: number) => {
      setView(prev => {
        const prevSpan = prev.end - prev.start
        const nextSpan = clamp(prevSpan * factor, MIN_SPAN_MS, fullSpan)
        if (nextSpan === prevSpan) return prev
        const f = clamp(fraction, 0, 1)
        const anchor = prev.start + f * prevSpan
        let start = anchor - f * nextSpan
        let end = start + nextSpan
        if (start < rangeStart) {
          start = rangeStart
          end = rangeStart + nextSpan
        }
        if (end > rangeEnd) {
          end = rangeEnd
          start = rangeEnd - nextSpan
        }
        return { start, end }
      })
    },
    [fullSpan, rangeStart, rangeEnd]
  )

  const reset = useCallback(
    () => setView({ start: rangeStart, end: rangeEnd }),
    [rangeStart, rangeEnd]
  )

  // Native, non-passive wheel listener: React's onWheel is passive, so it can't
  // preventDefault — without that the drawer scrolls instead of the axis zooming.
  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const fraction =
        rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5
      zoomAtFraction(fraction, e.deltaY < 0 ? 1 / WHEEL_STEP : WHEEL_STEP)
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => el.removeEventListener("wheel", onWheel)
  }, [zoomAtFraction])

  const onPointerDown = (e: React.PointerEvent) => {
    if (!isZoomed) return
    dragRef.current = { pointerId: e.pointerId, clientX: e.clientX, view }
    setDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current
    const el = trackRef.current
    if (!drag || !el) return
    const width = el.getBoundingClientRect().width || 1
    const dragSpan = drag.view.end - drag.view.start
    const deltaMs = ((e.clientX - drag.clientX) / width) * dragSpan
    const start = clamp(
      drag.view.start - deltaMs,
      rangeStart,
      rangeEnd - dragSpan
    )
    setView({ start, end: start + dragSpan })
  }
  const onPointerUp = (e: React.PointerEvent) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      e.currentTarget.releasePointerCapture(e.pointerId)
      dragRef.current = null
      setDragging(false)
    }
  }

  if (nodes.length === 0) {
    return (
      <Typography
        variant="caption"
        sx={{ color: extraColors.hint, display: "block", py: 2 }}
      >
        No reporting in this range.
      </Typography>
    )
  }

  const projectedPct = positionPct(rangeEnd, view)
  const visibleNodes = nodes.filter(
    node => node.t >= view.start && node.t <= view.end
  )

  return (
    <Box>
      <Box
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          position: "relative",
          height: TRACK_HEIGHT,
          overflow: "hidden",
          touchAction: "none",
          cursor: isZoomed ? (dragging ? "grabbing" : "grab") : "default",
        }}
      >
        {/* Baseline axis. */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: "1px",
            bgcolor: V2_SURFACE.hairlineStrong,
          }}
        />
        {visibleNodes.map(node => {
          const size = dotSize(node.count)
          return (
            <Tooltip
              key={node.t}
              arrow
              placement="top"
              title={<ArrivalTooltip node={node} />}
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: extraColors.dark,
                    border: `1px solid ${extraColors.translucent8}`,
                    maxWidth: 320,
                  },
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: `${positionPct(node.t, view)}%`,
                  width: size,
                  height: size,
                  borderRadius: "50%",
                  transform: "translate(-50%, -50%)",
                  bgcolor: V2_ACCENT.cyan,
                  border: `1px solid ${extraColors.dark}`,
                  boxShadow: `0 0 6px ${V2_ACCENT.cyanSoft}`,
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: V2_ACCENT.cyan,
                    filter: "brightness(1.3)",
                  },
                }}
              />
            </Tooltip>
          )
        })}
        {projectedLabel && projectedPct >= 0 && projectedPct <= 100 && (
          <Tooltip arrow placement="top" title={projectedLabel}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${projectedPct}%`,
                width: 0,
                borderLeft: `2px dashed ${V2_ACCENT.gold}`,
                opacity: 0.8,
              }}
            />
          </Tooltip>
        )}

        <Stack
          direction="row"
          spacing={0.5}
          // Keep clicks on the controls from starting a pan on the track (the
          // pointer capture would otherwise swallow the button's click).
          onPointerDown={e => e.stopPropagation()}
          sx={{ position: "absolute", top: 2, right: 2 }}
        >
          <ZoomButton
            label="Zoom out"
            onClick={() => zoomAtFraction(0.5, BUTTON_STEP)}
            disabled={!isZoomed}
          >
            <RemoveIcon sx={{ fontSize: 14 }} />
          </ZoomButton>
          <ZoomButton label="Reset zoom" onClick={reset} disabled={!isZoomed}>
            <RestartAltIcon sx={{ fontSize: 14 }} />
          </ZoomButton>
          <ZoomButton
            label="Zoom in"
            onClick={() => zoomAtFraction(0.5, 1 / BUTTON_STEP)}
            disabled={!canZoomIn}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </ZoomButton>
        </Stack>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ color: extraColors.translucent50, fontSize: "0.6rem" }}
        >
          {formatLocalDateTime(dayjsUTC(view.start), "MMM D")}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: extraColors.translucent50, fontSize: "0.6rem" }}
        >
          {formatLocalDateTime(dayjsUTC(view.end), "MMM D")}
        </Typography>
      </Box>
    </Box>
  )
}

function ZoomButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled: boolean
  children: React.ReactNode
}) {
  return (
    <Tooltip arrow title={label}>
      {/* span keeps the tooltip working while the button is disabled. */}
      <span>
        <IconButton
          size="small"
          aria-label={label}
          onClick={onClick}
          disabled={disabled}
          sx={{
            width: 22,
            height: 22,
            color: V2_ACCENT.cyan,
            border: `1px solid ${V2_SURFACE.hairlineStrong}`,
            borderRadius: "4px",
            bgcolor: "rgba(8,11,17,0.7)",
            "&:hover": { bgcolor: "rgba(55,178,255,0.12)" },
            "&.Mui-disabled": {
              color: extraColors.translucent50,
              opacity: 0.5,
            },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  )
}
